import { Component } from '@theme/component';
import { debounce, isTouch } from '@theme/utilities';
import { getFocusableElements } from '@theme/focus';

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #lastScrollY = window.scrollY;
  #scrollRafId = null;
  #pointerRafId = null;

  #dragStartX = 0;
  #dragStartY = 0;
  #dragLastX = 0;
  /** @type {'x' | 'y' | null} */
  #dragAxis = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {ResizeObserver | null} */
  #contentResizeObserver = null;

  /** @type {boolean} */
  #hasInited = false;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;


    this.setAttribute('data-paused', '');
    window.addEventListener('resize', this.#handleResize);
    this.#observeVisibility();

    // Keep the copy count and the duration in sync with the real content width.
    // The first measurement often lands before the items have their final size —
    // an unloaded image reports 0 — which used to leave the marquee stuck on the
    // single copy that a 0-width item asks for.
    this.#contentResizeObserver = new ResizeObserver(() => {
      if (this.#hasInited) this.#syncCopies();
      else this.#updateSpeed();
    });
    this.#contentResizeObserver.observe(this.refs.content);

    if (this.hasAttribute('data-scroll-reverse') && !isTouch()) {
      this.#lastScrollY = window.scrollY;
      window.addEventListener('scroll', this.#handleScroll, { passive: true });
    }

    // In the theme editor, selecting a block injects an overlay that steals the
    // :hover hit-test, so CSS hover-pause becomes unreliable. Track the pointer
    // geometrically instead (viewport coords are immune to the overlay).
    if (window.Shopify?.designMode) {
      document.addEventListener('pointermove', this.#handleEditorPointer, { passive: true });
    }

    if (isTouch()) {
      this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
      this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
      this.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    window.removeEventListener('scroll', this.#handleScroll);
    document.removeEventListener('pointermove', this.#handleEditorPointer);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('touchcancel', this.#handleTouchEnd);
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
    if (this.#pointerRafId) cancelAnimationFrame(this.#pointerRafId);
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#contentResizeObserver?.disconnect();
    this.#contentResizeObserver = null;

    this.#resetDom();
    // The DOM is back to a single copy, so a reconnect — the editor moving the
    // element when a section or block is reordered — has to build it again.
    this.#hasInited = false;
  }

  /**
   * Single IntersectionObserver: init when in viewport, pause when off-screen.
   * Replaces separate observers for visibility + numberOfCopies.
   */
  #observeVisibility() {
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          if (!this.#hasInited) {
            this.#loadImagesEagerly();
            this.#hasInited = true;
            this.#syncCopies();
          }
          this.removeAttribute('data-paused');
        } else {
          const { top, bottom } = this.getBoundingClientRect();
          const margin = 100;
          if (top < window.innerHeight + margin && bottom > -margin) return;
          this.setAttribute('data-paused', '');
        }
      },
      { root: null, rootMargin: '100px 0px', threshold: 0 }
    );
    this.#visibilityObserver.observe(this);
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * Force marquee images to load eagerly. Native lazy-loading (and the lazy
   * image-component observer) tracks an image's layout position, not its
   * animated transform, so images parked off-screen — or clipped to 0 inside the
   * overflow:hidden marquee on iOS / in the editor — never load on their own.
   * Matches Horizon, which renders its marquee logos eager and undimensioned-by-
   * lazy. Runs before the copies are cloned so the clones inherit the attribute.
   */
  #loadImagesEagerly() {
    for (const img of this.querySelectorAll('img[loading="lazy"]')) {
      img.loading = 'eager';
    }
  }

  /**
   * Synchronous dimensions; use when element is laid out (in viewport or after resize).
   */
  #getNumberOfCopies() {
    const { marqueeItems } = this.refs;
    if (!marqueeItems[0]) return 1;

    const marqueeWidth = this.getBoundingClientRect().width;
    const firstItemWidth = marqueeItems[0].getBoundingClientRect().width;

    return firstItemWidth > 0 ? Math.max(2, Math.ceil(marqueeWidth / firstItemWidth)) : 1;
  }

  /**
   * Map `data-speed-factor` (0.1–2) to a target scroll speed in pixels/second.
   * Higher factor → faster. Decoupled from content width on purpose.
   */
  #getPixelsPerSecond() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 1;
    const minPxPerSecond = Number(this.getAttribute('data-min-speed')) || 20.5;
    const maxPxPerSecond = Number(this.getAttribute('data-max-speed')) || 310;

    const minSpeedFactor = 0.1;
    const maxSpeedFactor = 2;
    const normalized = (speedFactor - minSpeedFactor) / (maxSpeedFactor - minSpeedFactor);
    const clamped = Math.max(0, Math.min(1, normalized));

    return minPxPerSecond + (maxPxPerSecond - minPxPerSecond) * clamped;
  }

  /**
   * Set the animation duration from the real content width so the marquee always
   * scrolls at a constant pixels/second. The keyframe translates exactly one
   * content width per cycle (`-50%` of the wrapper), so duration = width / speed.
   * This neutralises both an over-estimated copy count and the lazy-image race
   * that previously made the marquee occasionally run very fast.
   */
  #updateSpeed() {
    const { content } = this.refs;
    if (!content) return;

    const distance = content.getBoundingClientRect().width;
    if (distance <= 0) return;

    const duration = Math.max(2, Math.min(600, distance / this.#getPixelsPerSecond()));
    this.style.setProperty('--marquee-speed', `${duration}s`);
  }

  #handleResize = debounce(() => {
    if (!this.#hasInited) return;

    // A resize that leaves the copy count alone — a vertical one, or the mobile URL bar —
    // must not restart the animation.
    if (this.#syncCopies()) this.#restartAnimation();
  }, 250);

  #handleScroll = () => {
    if (this.#scrollRafId) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      const currentY = window.scrollY;
      let delta = currentY - this.#lastScrollY;
      this.#lastScrollY = currentY;

      delta = Math.max(-50, Math.min(50, delta));

      const scrollFactor = parseFloat(this.dataset.scrollFactor) || 3;
      const reverseFactor = parseFloat(this.dataset.scrollReverseFactor) || 6;
      const factor = delta < 0 ? reverseFactor : scrollFactor;
      const animations = this.refs.wrapper.getAnimations();

      for (const animation of animations) {
        const duration = animation.effect?.getComputedTiming()?.duration || 15000;
        const newTime = animation.currentTime + delta * factor;
        animation.currentTime = ((newTime % duration) + duration) % duration;
      }
    });
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    this.setAttribute('data-touch-paused', '');
    const touch = event.touches[0];
    if (!touch) return;
    this.#dragStartX = this.#dragLastX = touch.clientX;
    this.#dragStartY = touch.clientY;
    this.#dragAxis = null;
  };

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    const touch = event.touches[0];
    if (!touch) return;

    if (this.#dragAxis === null) {
      const dx = Math.abs(touch.clientX - this.#dragStartX);
      const dy = Math.abs(touch.clientY - this.#dragStartY);
      if (dx < 6 && dy < 6) return;
      this.#dragAxis = dx > dy ? 'x' : 'y';
    }

    if (this.#dragAxis !== 'x') return;

    event.preventDefault();
    this.#dragBy(touch.clientX - this.#dragLastX);
    this.#dragLastX = touch.clientX;
  };

  #handleTouchEnd = () => {
    this.removeAttribute('data-touch-paused');
    this.#dragAxis = null;
  };

  /** @param {number} deltaPx */
  #dragBy(deltaPx) {
    if (!deltaPx) return;

    const cycleDistance = this.refs.wrapper.getBoundingClientRect().width / 2;
    if (cycleDistance <= 0) return;

    const directionSign = this.dataset.direction === 'right' ? 1 : -1;

    for (const animation of this.refs.wrapper.getAnimations()) {
      const duration = Number(animation.effect?.getComputedTiming()?.duration) || 0;
      if (!duration) continue;

      const deltaTime = (deltaPx / cycleDistance) * duration * directionSign;
      const newTime = Number(animation.currentTime) + deltaTime;
      animation.currentTime = ((newTime % duration) + duration) % duration;
    }
  }

  /**
   * Geometric hover detection for the theme editor. Pauses while the pointer is
   * inside the component bounds, regardless of which element is the hit-test target.
   * @param {PointerEvent} event
   */
  #handleEditorPointer = (event) => {
    if (this.#pointerRafId) return;
    const { clientX, clientY } = event;
    this.#pointerRafId = requestAnimationFrame(() => {
      this.#pointerRafId = null;
      const { top, right, bottom, left } = this.getBoundingClientRect();
      const isInside = clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
      this.toggleAttribute('data-hover-paused', isInside);
    });
  };

  /**
   * Remove one marquee item by an inner element reference (e.g. countdown timer).
   * Called by announcement-bar when a countdown reaches zero.
   * @param {HTMLElement} target
   */
  removeItem(target) {
    if (!target) return;

    const source = this.refs.marqueeItems?.[0];
    if (!source) return;

    if (!source.contains(target)) return;
    target.remove();

    this.#resetDom();
    this.#hasInited = false;

    if (!source.firstElementChild) {
      this.setAttribute('data-paused', '');
      return;
    }

    this.#hasInited = true;
    this.#syncCopies();
    this.removeAttribute('data-paused');
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    const focusableElements = getFocusableElements(clone);
    focusableElements.forEach((element) => {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('tabindex', '-1');
    });

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Reset DOM structure to the original single set of items.
   */
  #resetDom() {
    const { content } = this.refs;
    if (!content) return;

    // Remove duplicated content clone
    this.clonedContent?.remove();

    // Keep only the first child in content (original marquee items wrapper)
    const children = Array.from(content.children);
    for (let i = 1; i < children.length; i++) {
      children[i].remove();
    }
  }

  /**
   * Re-measure, bring the copy count to what the current widths ask for, and refresh the
   * duplicate and the duration. Idempotent: it only touches the DOM when the count moved,
   * so the content ResizeObserver can call it without looping.
   *
   * @returns {boolean} Whether the copy count changed.
   */
  #syncCopies() {
    const numberOfCopies = this.#getNumberOfCopies();
    const currentNumberOfCopies = this.refs.content.children.length;
    const hasChanged = numberOfCopies !== currentNumberOfCopies;

    if (hasChanged) this.#setCopyCount(numberOfCopies);
    if (hasChanged || !this.clonedContent) this.#duplicateContent();

    this.#updateSpeed();

    return hasChanged;
  }

  /**
   * Grow or shrink the repeated items until exactly `total` copies sit inside the content.
   *
   * @param {number} total
   */
  #setCopyCount(total) {
    const { content, marqueeItems } = this.refs;
    const source = marqueeItems[0];

    if (!source) return;

    while (content.children.length > Math.max(1, total)) content.lastElementChild?.remove();
    while (content.children.length < total) content.appendChild(source.cloneNode(true));
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
